import {
  BodyBox,
  Button,
  Notice,
  PaginatedDataTable,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { useMutation } from '@tanstack/react-query';
import { FunctionComponent, useState } from 'react';

import { routes } from '~/client/config/routes';
import { ActivityReportFilters, OfficerActivityResult } from '~/common/types/activityReport';

import { SectionContainer } from '../../common/SectionContainer';
import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { DownloadButtonContainer, FiltersAndStatsContainer } from './ActivityReportPage.styled';
import {
  headings,
  mobileHeadings,
  filterConfigs,
  mapResultToRow,
  cellStyles,
  pageSize,
  title,
} from './config';
import { downloadKeyOutcomesReport } from './helpers';
import { TopLevelStats } from './TopLevelStats';
import { ActivityReportColumnId } from './types';
import { useActivityReport } from './useActivityReport';

export const ActivityReportPage: FunctionComponent = () => {
  const [dateRange, setDateRange] = useState<[string, string]>();

  const keyOutcomesReport = useMutation({
    mutationFn: () => downloadKeyOutcomesReport(...(dateRange || [])),
  });

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: mobileHeadings,
    sm: mobileHeadings,
  });

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[{ name: 'Home', link: routes.home }, { name: title }]}
      />
      <SectionContainer title={title}>
        <PaginatedDataTable<OfficerActivityResult, ActivityReportColumnId, ActivityReportFilters>
          renderFilterControls={({ filterControls }) => (
            <FiltersAndStatsContainer>
              {filterControls.dateRange}
              <TopLevelStats dateRange={dateRange} />
              {filterControls.officerName}
            </FiltersAndStatsContainer>
          )}
          headings={visibleHeadings}
          filterConfigs={filterConfigs}
          mapItemToRow={mapResultToRow}
          cellStyles={cellStyles}
          pageSize={pageSize}
          defaultSort={{ field: 'officerName', direction: 'asc' }}
          usePaginatedData={useActivityReport}
          onFiltersChange={(filters) => setDateRange(filters.dateRange)}
        />

        <DownloadButtonContainer>
          {keyOutcomesReport.isError && (
            <Notice type="error">
              Report generation failed. If the problem persists, please contact support.
            </Notice>
          )}
          <Button loading={keyOutcomesReport.isPending} onClick={() => keyOutcomesReport.mutate()}>
            Download key outcomes report
          </Button>
        </DownloadButtonContainer>
      </SectionContainer>
    </BodyBox>
  );
};
